import { storeCurrency } from '~/helpers/storeCurrency';
import { readonly, ref } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import { isNeedPrepositionTruthy } from '~/helpers/needPricePrefix';
import { isHiddenSearchAttribute } from '~/helpers/search/isHiddenSearchAttribute';
import { isGiftCardProduct } from '~/helpers/isGiftCardProduct';
import useApi from '~/composables/useApi';
import { getProductSearchQuery } from './getProductSearch.gql';
import { useWebsiteCode } from '~/composables/useWebsiteCode';
import getSearchSortFieldsQuery from './getSearchSortFields.gql';
import { mapSortFieldsToOptions } from './sortingOptions';
import type { SortingOption } from './sortingOptions';
import type { Aggregation, AggregationOption, SortFields } from '~/modules/GraphQL/types';
import type {
  SearchResultData,
  UseFacetInterface, UseFacetErrors, UseFacetSearchResult, FacetSearchParams,
} from './useFacet';

/**
 * Interface for SearchProducts response
 */
interface SearchProductsResponse {
  SearchProducts: {
    zeroQueries: boolean;
    success?: boolean;
    redirect_url?: string | null;
    count?: number;
    sts?: Array<{ st?: string | null }>;
    header_block?: {
      content?: string;
      show?: boolean;
    };
    categories?: Array<{
      id?: string | number;
      name?: string;
      link_url?: string;
      count?: number;
    }>;
    filters?: SearchProductsFilter[];
    products: Array<{
      id: string;
      sku?: string;
      available: boolean;
      name: string;
      link_url: string;
      image_url: string;
      hover_url?: string | null;
      price: number;
      oldPrice?: number | null;
      badge?: string | null;
      need_preposition?: boolean | null;
      score: number;
      type: string;
    }>;
  };
}

interface SearchProductsFilterValue {
  id?: string | number | null;
  name?: string | number | null;
  value?: string | number | null;
}

interface SearchProductsFilter {
  dataType?: string | null;
  name?: string | null;
  code?: string | null;
  values?: SearchProductsFilterValue[] | null;
}

type SearchProductsProduct = SearchProductsResponse['SearchProducts']['products'][number];

const LISTING_DEFAULT_SORT_VALUE = 'sorting_position_DESC';
const LISTING_SORT_ORDER = [
  'new_DESC',
  'position_DESC',
  'price_asc_DESC',
  'price_desc_DESC',
  'saving_DESC',
  LISTING_DEFAULT_SORT_VALUE,
];

const normalizeSearchSortOptions = (options: SortingOption[]): SortingOption[] => {
  const order = new Map(LISTING_SORT_ORDER.map((value, index) => [value, index]));
  const hasListingDefault = options.some(({ value }) => value === LISTING_DEFAULT_SORT_VALUE);

  return [...options]
    .sort((left, right) => (
      (order.get(left.value) ?? LISTING_SORT_ORDER.length)
      - (order.get(right.value) ?? LISTING_SORT_ORDER.length)
    ))
    .map((option) => ({
      ...option,
      isDefault: hasListingDefault
        ? option.value === LISTING_DEFAULT_SORT_VALUE
        : option.isDefault,
    }));
};

/**
 * Interface for filter format expected by SearchProducts API
 */
interface SearchFilter {
  attribute_name: string;
  values: string[];
}

export const buildSearchProductsFilters = (filters?: FacetSearchParams['filters']): SearchFilter[] => {
  const searchFilters: SearchFilter[] = [];

  if (!filters) {
    return searchFilters;
  }

  Object.entries(filters).forEach(([attributeName, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      const normalizedValues = attributeName === 'price'
        ? values.flatMap((value) => String(value).split('_')).filter(Boolean)
        : values as string[];

      searchFilters.push({
        attribute_name: attributeName,
        values: normalizedValues,
      });
    }
  });

  return searchFilters;
};

const normalizeTextValue = (value: unknown): string => (
  typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : ''
);

const normalizeNumericValue = (value: unknown): number | null => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const resolvePriceRangeValue = (
  values: SearchProductsFilterValue[] = [],
  targetId: 'min' | 'max',
): number | null => {
  const matchedValue = values.find((value) => normalizeTextValue(value?.id).toLowerCase() === targetId);

  if (!matchedValue) {
    return null;
  }

  return normalizeNumericValue(matchedValue.value) ?? normalizeNumericValue(matchedValue.name);
};

const resolvePriceStep = (min: number, max: number): number => {
  const range = Math.max(max - min, 0);

  if (range >= 100000) {
    return 1000;
  }

  if (range >= 10000) {
    return 100;
  }

  if (range >= 1000) {
    return 10;
  }

  return 1;
};

const resolveProductPriceRange = (products: SearchProductsProduct[] = []) => {
  const prices = products
    .map((product) => normalizeNumericValue(product?.price))
    .filter((price): price is number => price !== null && price > 0);

  if (!prices.length) {
    return null;
  }

  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
};

const createPriceAggregation = (
  filter: SearchProductsFilter,
  products: SearchProductsProduct[] = [],
): Aggregation | null => {
  const values = Array.isArray(filter.values) ? filter.values : [];
  const minValue = resolvePriceRangeValue(values, 'min');
  const maxValue = resolvePriceRangeValue(values, 'max');
  const productPriceRange = resolveProductPriceRange(products);

  if ((minValue === null || maxValue === null) && !productPriceRange) {
    return null;
  }

  const resolvedMin = minValue && minValue > 0
    ? minValue
    : productPriceRange?.min ?? minValue;
  const resolvedMax = maxValue && maxValue > 0
    ? maxValue
    : productPriceRange?.max ?? maxValue;

  if (resolvedMin === null || resolvedMax === null) {
    return null;
  }

  const min = Math.min(resolvedMin, resolvedMax);
  const max = Math.max(resolvedMin, resolvedMax);
  const priceRange = {
    min,
    max,
    from: min,
    to: max,
    step: resolvePriceStep(min, max),
  } as unknown as Aggregation['price_range'];

  return {
    attribute_code: 'price',
    label: 'Цена',
    count: values.length,
    options: [],
    price_range: priceRange,
  };
};

const createDistinctAggregationOption = (
  rawOption: SearchProductsFilterValue,
): AggregationOption | null => {
  const value = normalizeTextValue(rawOption?.id) || normalizeTextValue(rawOption?.name);
  const label = normalizeTextValue(rawOption?.name) || value;

  if (!value || !label) {
    return null;
  }

  return {
    value,
    label,
    count: normalizeNumericValue(rawOption?.value) ?? 0,
    alias: '',
  };
};

export const transformSearchProductsFiltersToAggregations = (
  filters: SearchProductsFilter[] = [],
  products: SearchProductsProduct[] = [],
  isBy = false,
): Aggregation[] => (
  (filters ?? [])
    .map((filter) => {
      const attributeCode = isBy
        ? normalizeTextValue(filter?.code) || normalizeTextValue(filter?.name)
        : normalizeTextValue(filter?.name);
      const dataType = normalizeTextValue(filter?.dataType).toUpperCase();

      if (!attributeCode || isHiddenSearchAttribute(attributeCode)) {
        return null;
      }

      if (dataType === 'SLIDER') {
        return createPriceAggregation(filter, products);
      }

      const options = (Array.isArray(filter?.values) ? filter.values : [])
        .map((option) => createDistinctAggregationOption(option))
        .filter(Boolean) as AggregationOption[];

      return {
        attribute_code: attributeCode,
        label: isBy ? normalizeTextValue(filter?.name) || attributeCode : attributeCode,
        count: options.length,
        options,
      } as Aggregation;
    })
    .filter(Boolean) as Aggregation[]
);

/**
 * Transforms SearchProducts API response to ProductInterface format
 */
export function transformSearchProductsToProductInterface(products: SearchProductsResponse['SearchProducts']['products']): any[] {
  return products.map(product => {
    const rawPath = product.link_url.replace(/^https?:\/\/[^\/]+/, '');
    const localeStrippedPath = rawPath.replace(/^\/[a-z]{2}\//i, '/');
    const normalizedPath = localeStrippedPath.replace(/^\/+/, '');

    return {
      uid: product.id,
      id: product.id,
      sku: product.sku || product.id,
      name: product.name,
      stock_status: product.available ? 'IN_STOCK' : 'OUT_OF_STOCK',
      price_range: {
        minimum_price: {
          regular_price: {
            value: product.oldPrice || product.price,
            currency: storeCurrency,
          },
          final_price: {
            value: product.price,
            currency: storeCurrency,
          },
        },
        maximum_price: {
          regular_price: {
            value: product.oldPrice || product.price,
            currency: storeCurrency,
          },
          final_price: {
            value: product.price,
            currency: storeCurrency,
          },
        },
      },
      image: {
        url: product.image_url,
      },
      thumbnail: {
        url: product.image_url,
      },
      small_image: {
        url: product.image_url,
      },
      product_thumbnail: product.image_url,
      product_hover_image: product.hover_url || null,
      badges: product.badge ? [{ label: product.badge }] : [],
      url_key: normalizedPath,
      url_rewrites: [{
        url: normalizedPath,
      }],
      __typename: product.type,
      is_gift_card: isGiftCardProduct(product),
      need_preposition: isNeedPrepositionTruthy(product.need_preposition) ? true : undefined,
    };
  });
}

export const loadSearchProductsData = async (
  queryFn: (query: string, variables?: Record<string, any>) => Promise<{
    data?: unknown;
    errors?: Array<{ message?: string }>;
  }>,
  params?: FacetSearchParams,
  isBy = false,
): Promise<SearchResultData> => {
  const searchParams: { search: string; filters: SearchFilter[]; sort?: string } = {
    search: params?.term || '',
    filters: buildSearchProductsFilters(params?.filters),
  };

  const sortValue = typeof params?.sort === 'string' && params.sort.trim()
    ? params.sort.trim()
    : '';

  if (sortValue) {
    searchParams.sort = sortValue;
  }

  const searchTerm = params?.term?.trim() || '';
  const shouldLoadSortOptions = params?.loadSortOptions !== false;
  const [searchProductsResponse, sortFieldsResponse] = await Promise.all([
    queryFn(getProductSearchQuery(isBy), searchParams),
    searchTerm && shouldLoadSortOptions
      ? queryFn(getSearchSortFieldsQuery, { search: searchTerm })
      : Promise.resolve({ data: null }),
  ]);
  const searchProducts = (searchProductsResponse?.data as SearchProductsResponse)?.SearchProducts ?? null;
  const searchErrorMessage = searchProductsResponse?.errors?.find(({ message }) => message)?.message;
  const sortFields = (sortFieldsResponse?.data as { products?: { sort_fields?: SortFields } })?.products?.sort_fields ?? null;
  Logger.debug('[Result]:', { searchProducts, sortFields });

  if (searchProductsResponse?.errors?.length || !searchProducts || searchProducts.success === false) {
    throw new Error(searchErrorMessage || 'Product search is temporarily unavailable');
  }

  const allProducts = searchProducts?.products
    ? transformSearchProductsToProductInterface(searchProducts.products)
    : [];

  const pageSize = params?.itemsPerPage || 36;
  const currentPage = params?.page || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedProducts = allProducts.slice(startIndex, endIndex);

  return {
    items: paginatedProducts,
    aggregations: transformSearchProductsFiltersToAggregations(searchProducts?.filters ?? [], searchProducts?.products ?? [], isBy),
    total: searchProducts?.count ?? allProducts.length,
    categories: searchProducts?.categories ?? [],
    sts: searchProducts?.sts ?? [],
    availableSortingOptions: normalizeSearchSortOptions(mapSortFieldsToOptions(sortFields)),
    perPageOptions: [],
    itemsPerPage: pageSize,
    products_header_block: searchProducts?.header_block,
    redirectUrl: searchProducts?.redirect_url?.trim() || undefined,
  };
};

/**
 * Allows searching for products with pagination, totals and sorting options.
 * This composable is specifically designed for product search functionality.
 *
 * See the {@link UseFacetInterface} for a list of methods and values available in this composable.
 */
export function useProductSearch(): UseFacetInterface {
  const { query } = useApi();
  const { isBy } = useWebsiteCode();
  const _loading = ref(false);
  const result = ref<UseFacetSearchResult>({ data: null, input: null });
  const _error = ref<UseFacetErrors>({
    search: null,
  });

  const search = async (params?: FacetSearchParams): Promise<UseFacetSearchResult> => {
    Logger.debug('useProductSearch/search', params);

    const searchResult: UseFacetSearchResult = {
      input: params ?? null,
      data: null,
    };

    try {
      _loading.value = true;
      _error.value.search = null;
      const searchData = await loadSearchProductsData(query, params, isBy.value);

      searchResult.data = searchData;
      result.value = searchResult;
      _error.value.search = null;
    } catch (err) {
      result.value = searchResult;
      _error.value.search = err instanceof Error ? err : new Error(String(err));
      Logger.error('useProductSearch/search', err);
    } finally {
      _loading.value = false;
    }

    return searchResult;
  };

  return {
    search,
    result,
    error: readonly(_error),
    loading: readonly(_loading),
  };
}

export * from './useFacet';
export default useProductSearch;
